if (!customElements.get('product-delivery-estimate')) {
  const US_ZIP_RANGES = [
    [5, 5, 'NY'],
    [10, 27, 'MA'],
    [28, 29, 'RI'],
    [30, 38, 'NH'],
    [39, 49, 'ME'],
    [50, 59, 'VT'],
    [60, 69, 'CT'],
    [70, 89, 'NJ'],
    [90, 98, 'AE'],
    [100, 149, 'NY'],
    [150, 196, 'PA'],
    [197, 199, 'DE'],
    [200, 205, 'DC'],
    [206, 219, 'MD'],
    [220, 246, 'VA'],
    [247, 268, 'WV'],
    [270, 289, 'NC'],
    [290, 299, 'SC'],
    [300, 319, 'GA'],
    [340, 340, 'AA'],
    [320, 349, 'FL'],
    [350, 369, 'AL'],
    [370, 385, 'TN'],
    [386, 397, 'MS'],
    [398, 399, 'GA'],
    [400, 427, 'KY'],
    [430, 459, 'OH'],
    [460, 479, 'IN'],
    [480, 499, 'MI'],
    [500, 528, 'IA'],
    [530, 549, 'WI'],
    [550, 567, 'MN'],
    [570, 577, 'SD'],
    [580, 588, 'ND'],
    [590, 599, 'MT'],
    [600, 629, 'IL'],
    [630, 658, 'MO'],
    [660, 679, 'KS'],
    [680, 693, 'NE'],
    [700, 729, 'LA'],
    [730, 749, 'OK'],
    [750, 799, 'TX'],
    [800, 816, 'CO'],
    [820, 831, 'WY'],
    [832, 838, 'ID'],
    [840, 847, 'UT'],
    [850, 865, 'AZ'],
    [870, 884, 'NM'],
    [889, 898, 'NV'],
    [900, 961, 'CA'],
    [962, 966, 'AP'],
    [967, 968, 'HI'],
    [970, 979, 'OR'],
    [980, 994, 'WA'],
    [995, 999, 'AK']
  ];

  const CA_POSTAL_PROVINCES = {
    A: ['NL'],
    B: ['NS'],
    C: ['PE'],
    E: ['NB'],
    G: ['QC'],
    H: ['QC'],
    J: ['QC'],
    K: ['ON'],
    L: ['ON'],
    M: ['ON'],
    N: ['ON'],
    P: ['ON'],
    R: ['MB'],
    S: ['SK'],
    T: ['AB'],
    V: ['BC'],
    X: ['NT', 'NU'],
    Y: ['YT']
  };

  class ProductDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-delivery-estimate-form]');
      this.country = this.querySelector('[data-delivery-country]');
      this.usStateField = this.querySelector('[data-us-state-field]');
      this.usState = this.querySelector('[data-us-state]');
      this.caProvinceField = this.querySelector('[data-ca-province-field]');
      this.caProvince = this.querySelector('[data-ca-province]');
      this.postal = this.querySelector('[data-delivery-postal]');
      this.postalLabel = this.querySelector('[data-postal-label]');
      this.error = this.querySelector('[data-delivery-error]');
      this.result = this.querySelector('[data-delivery-result]');
      this.cost = this.querySelector('[data-delivery-cost]');
      this.ships = this.querySelector('[data-delivery-ships]');
      this.arrives = this.querySelector('[data-delivery-arrives]');
      this.origin = (this.dataset.origin || '').toLowerCase();

      this.handleCountryChange = this.handleCountryChange.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleFieldChange = this.handleFieldChange.bind(this);

      this.country.addEventListener('change', this.handleCountryChange);
      this.form.addEventListener('submit', this.handleSubmit);
      this.form.addEventListener('input', this.handleFieldChange);
      this.form.addEventListener('change', this.handleFieldChange);

      this.country.value = this.dataset.defaultCountry === 'CA' ? 'CA' : 'US';
      this.updateCountryFields();
    }

    disconnectedCallback() {
      this.country?.removeEventListener('change', this.handleCountryChange);
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.form?.removeEventListener('input', this.handleFieldChange);
      this.form?.removeEventListener('change', this.handleFieldChange);
    }

    handleCountryChange() {
      this.updateCountryFields();
      this.resetResult();
    }

    handleFieldChange() {
      if (!this.result.hidden) {
        this.resetResult();
      }
    }

    updateCountryFields() {
      const isCanada = this.country.value === 'CA';
      this.usStateField.hidden = isCanada;
      this.usState.disabled = isCanada;
      this.caProvinceField.hidden = !isCanada;
      this.caProvince.disabled = !isCanada;
      this.postalLabel.textContent = isCanada ? 'Postal code' : 'ZIP code';
      this.postal.placeholder = isCanada ? 'e.g. K1A 0B1' : 'e.g. 98148';
      this.postal.maxLength = isCanada ? 7 : 10;
      this.postal.inputMode = isCanada ? 'text' : 'numeric';
    }

    resetResult() {
      this.error.hidden = true;
      this.result.hidden = true;
    }

    handleSubmit(event) {
      event.preventDefault();
      this.resetResult();

      const country = this.country.value;
      const province = country === 'CA' ? this.caProvince.value : this.usState.value;
      const postal = this.postal.value.trim().toUpperCase();
      const postalIsValid = country === 'CA'
        ? this.isValidCanadianPostalCode(postal)
        : this.isValidUsPostalCode(postal);
      const locationMatches = postalIsValid && this.locationMatchesPostalCode(country, province, postal);

      if (!province || !postalIsValid || !locationMatches) {
        this.error.textContent = country === 'CA'
          ? 'Choose a province or territory and enter its matching Canadian postal code.'
          : 'Choose a state and enter its matching ZIP code.';
        this.error.hidden = false;
        if (!province) {
          (country === 'CA' ? this.caProvince : this.usState).focus();
        } else if (!postalIsValid || !locationMatches) {
          this.postal.focus();
        }
        return;
      }

      this.renderEstimate(country);
      this.result.hidden = false;
    }

    isValidUsPostalCode(value) {
      return /^(?:00[5-9]|0[1-9]\d|[1-9]\d{2})\d{2}(?:-\d{4})?$/.test(value);
    }

    isValidCanadianPostalCode(value) {
      return /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/.test(value);
    }

    locationMatchesPostalCode(country, province, postal) {
      if (country === 'CA') {
        const allowedProvinces = CA_POSTAL_PROVINCES[postal.charAt(0)] || [];
        return allowedProvinces.includes(province);
      }

      const prefix = Number(postal.slice(0, 3));
      const expectedState = US_ZIP_RANGES.find(([min, max]) => {
        return prefix >= min && prefix <= max;
      });
      return expectedState?.[2] === province;
    }

    renderEstimate(country) {
      const offsets = this.origin === 'portugal'
        ? { shipMin: 15, shipMax: 29, arrivalMin: 21, arrivalMax: 35 }
        : { shipMin: 1, shipMax: 4, arrivalMin: 9, arrivalMax: 13 };
      const today = this.startOfToday();

      this.cost.textContent = country === 'CA' && this.origin === 'us'
        ? 'Calculated at checkout'
        : 'Free';
      this.ships.textContent = this.formatRange(
        this.addDays(today, offsets.shipMin),
        this.addDays(today, offsets.shipMax),
        today.getFullYear()
      );
      this.arrives.textContent = this.formatRange(
        this.addDays(today, offsets.arrivalMin),
        this.addDays(today, offsets.arrivalMax),
        today.getFullYear()
      );
    }

    startOfToday() {
      const date = new Date();
      date.setHours(0, 0, 0, 0);
      return date;
    }

    addDays(date, days) {
      const result = new Date(date);
      result.setDate(result.getDate() + days);
      return result;
    }

    formatRange(start, end, currentYear) {
      const showYear = start.getFullYear() !== currentYear || end.getFullYear() !== currentYear;
      const monthDay = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        ...(showYear ? { year: 'numeric' } : {})
      });

      if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
        return `${monthDay.format(start)}\u2013${new Intl.DateTimeFormat('en-US', { day: 'numeric' }).format(end)}`;
      }

      return `${monthDay.format(start)}\u2013${monthDay.format(end)}`;
    }
  }

  customElements.define('product-delivery-estimate', ProductDeliveryEstimate);
}
